import { LinkedInPost, PaperLite, RepoLite } from '../../../services/api'
import {
    FeedCard,
    FeedCardBody,
    FeedCardFooter,
    ClampedText,
    MutedText,
    BadgeRow,
    Chip,
    ChipButton,
    ExternalLink,
    compactCount,
    mentionQualifier,
    paletteFor,
    repoIsGone,
    repoLabel,
} from './linkedinStyles'

/**
 * One entry of the unified SOTA list: a piece of content somebody shared, with
 * badges for what it carries. There is one card type because there is one list —
 * a repo and a paper are things a post *has*, not separate rows.
 *
 * The badges come inline with the list row (`post.repos` / `post.papers`), not
 * from a per-entry detail request: one page of twelve entries used to cost up to
 * thirteen HTTP calls and painted its badges in a second pass.
 */

const formatDate = (value?: string | null): string => {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
}

interface PostCardProps {
    post: LinkedInPost
    onOpenDetail: (type: 'repo' | 'paper' | 'post', id: number) => void
}

/** A repo badge: owner/name, stars, and whatever qualifies the attribution. */
const RepoChip: React.FC<{ repo: RepoLite; onOpen: () => void }> = ({ repo, onOpen }) => {
    const qualifier = mentionQualifier(repo.mention_type)
    const gone = repoIsGone(repo)
    const hint = [repoLabel(repo), gone ? 'Ya no existe en GitHub' : null, qualifier?.hint]
        .filter(Boolean)
        .join(' — ')
    return (
        <ChipButton
            type="button"
            // A repo GitHub no longer serves is history, not a project to go and
            // look at, and green with a star count reads as the latter.
            $bg={gone ? '#ECEFF1' : '#E8F5E9'}
            $color={gone ? '#607D8B' : '#2E7D32'}
            title={hint}
            onClick={(e) => {
                e.stopPropagation()
                onOpen()
            }}
        >
            💻 {repoLabel(repo)}
            {!gone && typeof repo.stars === 'number' && ` · ⭐ ${compactCount(repo.stars)}`}
            {gone && ' · no disponible'}
            {qualifier && ` · ${qualifier.suffix}`}
        </ChipButton>
    )
}

/** A paper badge. Carries the same qualifier as a repo: a paper reached the post
 *  through the same two routes, one of which rests on an identity match. */
const PaperChip: React.FC<{ paper: PaperLite; onOpen: () => void }> = ({ paper, onOpen }) => {
    const qualifier = mentionQualifier(paper.mention_type)
    return (
        <ChipButton
            type="button"
            $bg="#E3F2FD"
            $color="#1565C0"
            title={[paper.title, qualifier?.hint].filter(Boolean).join(' — ')}
            onClick={(e) => {
                e.stopPropagation()
                onOpen()
            }}
        >
            📄 {paper.source || 'paper'}
            {qualifier && ` · ${qualifier.suffix}`}
        </ChipButton>
    )
}

export const PostCard: React.FC<PostCardProps> = ({ post, onOpenDetail }) => {
    const body = post.summary || post.content || ''
    const date = formatDate(post.published_at || post.created_at)

    const repos = post.repos || []
    const papers = post.papers || []
    // The flag on the post row is what the filters query, so it decides whether a
    // badge appears at all. A generic badge covers the case where the two
    // disagree (a link classified as a repo whose row never materialised) —
    // never blank, never contradicting the filter the reader just applied.
    const showGenericRepo = !!post.has_repo_link && repos.length === 0
    const showGenericPaper = !!post.has_paper_link && papers.length === 0
    const carriesNothing = !post.has_repo_link && !post.has_paper_link

    const openPost = () => onOpenDetail('post', post.id)

    return (
        <FeedCard>
            <FeedCardBody
                role="button"
                tabIndex={0}
                // Without a label the card announces its whole body as the button
                // name, which buries the badge buttons nested inside it.
                aria-label={`Abrir detalle de la publicación de ${post.author || 'autor desconocido'}`}
                style={{ cursor: 'pointer' }}
                onClick={openPost}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        openPost()
                    }
                }}
            >
                <MutedText>
                    {post.author ? <strong>{post.author}</strong> : <em>Autor desconocido</em>}
                    {date && ` · ${date}`}
                </MutedText>

                <BadgeRow>
                    {repos.map((r) => (
                        <RepoChip key={`r-${r.id}`} repo={r} onOpen={() => onOpenDetail('repo', r.id)} />
                    ))}
                    {showGenericRepo && <Chip $bg="#E8F5E9" $color="#2E7D32">💻 Repo</Chip>}

                    {papers.map((p) => (
                        <PaperChip key={`p-${p.id}`} paper={p} onOpen={() => onOpenDetail('paper', p.id)} />
                    ))}
                    {showGenericPaper && <Chip $bg="#E3F2FD" $color="#1565C0">📄 Paper</Chip>}

                    {/* Most ingested posts are commentary with nothing attached. Saying so
                        is better than a card whose badge row is simply empty. */}
                    {carriesNothing && <Chip $bg="#ECEFF1" $color="#607D8B">Sin repo ni paper</Chip>}

                    {/* Health, forks, licence and the rest belong to the repo, not to the
                        piece of content: they are in the modal. Movement is the exception —
                        it is the reason to look at this row today rather than next month. */}
                    {repos.some((r) => r.is_trending && !repoIsGone(r)) && (
                        <Chip $bg="#FCE4EC" $color="#C2185B">🔥 Tendencia</Chip>
                    )}
                </BadgeRow>

                {body && <ClampedText $lines={4}>{body}</ClampedText>}

                {(post.category || (post.tags && post.tags.length > 0)) && (
                    <BadgeRow>
                        {post.category && (
                            <Chip $bg={paletteFor(post.category).bg} $color={paletteFor(post.category).color}>
                                {post.category}
                            </Chip>
                        )}
                        {(post.tags || []).map((t) => {
                            const p = paletteFor(t)
                            return (
                                <Chip key={t} $bg={p.bg} $color={p.color}>#{t}</Chip>
                            )
                        })}
                    </BadgeRow>
                )}
            </FeedCardBody>

            <FeedCardFooter>
                <MutedText>{typeof post.reactions === 'number' ? `👍 ${post.reactions}` : ''}</MutedText>
                {post.linkedin_url && (
                    <ExternalLink
                        href={post.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        Ver en LinkedIn ↗
                    </ExternalLink>
                )}
            </FeedCardFooter>
        </FeedCard>
    )
}

export default PostCard
